import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { usePageTitle } from '../../hooks/usePageTitle';
import { adminApi } from '../../api/client';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { getApiErrorMessage } from '../../api/errors';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogBody, DialogFooter,
} from '../../components/ui/dialog';

function useAdminUsers() {
  return useQuery({
    queryKey: ['admin', 'users'],
    queryFn: async () => {
      const { data } = await adminApi.getUsers(1, 100);
      if (!data.success) throw new Error(data.message || 'Failed to load users');
      return data.data || [];
    },
  });
}

const ROLES = ['User', 'ContentCreator', 'Admin'];

const ROLE_STYLES = {
  Admin: 'bg-primary/10 text-primary ring-primary/25',
  ContentCreator: 'bg-sky-500/10 text-sky-400 ring-sky-500/25',
  User: 'bg-white/5 text-muted-foreground ring-white/10',
};

const inputClass =
  'w-full rounded-lg border border-white/10 bg-white/[0.03] px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground/50 outline-none transition-colors focus:border-primary/50 focus:bg-white/[0.05]';

function Field({ label, children }) {
  return (
    <div>
      <label className="mb-1.5 block text-xs font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}

function EmptyState({ icon, children }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 rounded-xl py-16 text-center ring-1 ring-foreground/[0.06]">
      <Icon icon={icon} className="size-6 text-muted-foreground/40" />
      <p className="text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function RoleBadge({ role }) {
  const style = ROLE_STYLES[role] || 'bg-white/5 text-muted-foreground ring-white/10';
  return (
    <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ${style}`}>
      {role === 'Admin' && <Icon icon="ph:shield-check-bold" className="size-3" />}
      {role === 'ContentCreator' ? 'Content Creator' : role}
    </span>
  );
}

function EditUserModal({ user, onClose, onSave, saving }) {
  const [form, setForm] = useState({
    FullName: user.FullName || '',
    Username: user.Username || '',
    Email: user.Email || '',
    Role: user.Role || 'User',
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.FullName.trim()) { toast.error('Full name is required'); return; }
    if (!form.Username.trim()) { toast.error('Username is required'); return; }
    if (!form.Email.trim()) { toast.error('Email is required'); return; }
    onSave(form);
  };

  return (
    <Dialog open onOpenChange={(next) => { if (!next) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Icon icon="ph:pencil-simple-bold" />
            Edit User
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="contents">
          <DialogBody>
            <Field label="Full Name">
              <input
                className={inputClass}
                value={form.FullName}
                onChange={(e) => setForm((f) => ({ ...f, FullName: e.target.value }))}
                maxLength={100}
                autoFocus
              />
            </Field>
            <Field label="Username">
              <input
                className={inputClass}
                value={form.Username}
                onChange={(e) => setForm((f) => ({ ...f, Username: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '') }))}
                maxLength={20}
              />
            </Field>
            <Field label="Email">
              <input
                type="email"
                className={inputClass}
                value={form.Email}
                onChange={(e) => setForm((f) => ({ ...f, Email: e.target.value }))}
              />
            </Field>
            <Field label="Role">
              <Select value={form.Role} onValueChange={(value) => setForm((f) => ({ ...f, Role: value }))}>
                <SelectTrigger className="w-full">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLES.map((r) => (
                    <SelectItem key={r} value={r}>{r === 'ContentCreator' ? 'Content Creator' : r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </DialogBody>

          <DialogFooter>
            <Button type="button" variant="secondary" onClick={onClose} className="gap-1.5">
              <Icon icon="ph:x-bold" />
              Cancel
            </Button>
            <Button type="submit" disabled={saving} className="gap-1.5">
              <Icon icon="ph:check-bold" />
              {saving ? 'Saving…' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function AdminUsersPage() {
  usePageTitle('Manage Users · Kast');
  const { data: users = [], isLoading, error } = useAdminUsers();
  const qc = useQueryClient();
  const [editingUser, setEditingUser] = useState(null);

  const updateUserMutation = useMutation({
    mutationFn: ({ userId, data }) => adminApi.updateUser(userId, data),
    onSuccess: () => {
      toast.success('User updated.');
      qc.invalidateQueries(['admin', 'users']);
      setEditingUser(null);
    },
    onError: (error) => toast.error(getApiErrorMessage(error, 'Failed to update user')),
  });

  const handleSave = (form) => {
    updateUserMutation.mutate({ userId: editingUser.Id, data: form });
  };

  return (
    <div className="px-4 py-6 sm:px-6 sm:py-8 lg:px-10">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <span className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-white/5 text-primary ring-1 ring-white/10">
            <Icon icon="ph:users-bold" className="size-5" />
          </span>
          <div>
            <h1 className="text-lg font-semibold text-foreground">Users</h1>
            <p className="text-xs text-muted-foreground">Manage accounts, roles, and profile details</p>
          </div>
        </div>
        {users.length > 0 && (
          <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary ring-1 ring-primary/25">
            {users.length} user{users.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {isLoading ? (
        <EmptyState icon="ph:spinner-gap-bold">Loading…</EmptyState>
      ) : error ? (
        <EmptyState icon="ph:warning-bold">{getApiErrorMessage(error, 'Failed to load users.')}</EmptyState>
      ) : users.length === 0 ? (
        <EmptyState icon="ph:users-bold">No users yet</EmptyState>
      ) : (
        <div className="overflow-x-auto rounded-xl ring-1 ring-foreground/[0.06]">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-b border-white/5 text-xs uppercase tracking-wider text-muted-foreground/60">
                <th className="px-4 py-3 font-medium">Name</th>
                <th className="px-4 py-3 font-medium">Username</th>
                <th className="px-4 py-3 font-medium">Email</th>
                <th className="px-4 py-3 font-medium">Role</th>
                <th className="px-4 py-3 font-medium">Joined</th>
                <th className="px-4 py-3 font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.Id} className="border-b border-white/5 transition-colors last:border-0 hover:bg-white/[0.02]">
                  <td className="px-4 py-3 font-medium text-foreground">{u.FullName}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.Username}</td>
                  <td className="px-4 py-3 text-muted-foreground">{u.Email}</td>
                  <td className="px-4 py-3"><RoleBadge role={u.Role} /></td>
                  <td className="px-4 py-3 text-muted-foreground">{u.DateCreated?.toString()?.split('T')[0]}</td>
                  <td className="px-4 py-3">
                    <Button size="sm" variant="secondary" onClick={() => setEditingUser(u)} className="gap-1.5">
                      <Icon icon="ph:pencil-simple-bold" />
                      Edit
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          onClose={() => setEditingUser(null)}
          onSave={handleSave}
          saving={updateUserMutation.isPending}
        />
      )}
    </div>
  );
}
